@import '../../globals/variables';

/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$switch-toggle-width: 36px !default;
$switch-toggle-small-width: 30px !default;
$switch-toggle-height: 22px !default;
$switch-toggle-small-height: 20px !default;
$switch-toggle-border-width: 2px !default;
$switch-toggle-border-hover-width: 3px !default;
$switch-toggle-small-border-width: 2px !default;
$switch-toggle-border-radius: 12px !default;
$switch-toggle-small-border-radius: 10px !default;
$switch-toggle-check-size: 12px !default;
$switch-toggle-small-check-size: 10px !default;
$switch-toggle-check-radius: 100% !default;
$switch-toggle-small-check-radius: 5px !default;
$switch-toggle-check-margin: 5px !default;
$switch-toggle-small-check-margin: 5px !default;
$switch-toggle-label-font-size: $copy-font-size !default;
$switch-toggle-small-label-font-size: $copy-small-font-size !default;
$switch-toggle-label-line-height: 24px !default;
$switch-toggle-small-label-line-height: 18px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: inline-block;
}

:host([disabled]:not([disabled="false"])) {
  cursor: not-allowed;
  label {
    pointer-events: none;
  }
}

::slotted(envc-eon-ui-rte-renderer) {
  --rteRendererLineHeight: #{$switch-toggle-label-line-height};
}

.#{$prefix}switch-toggle {
  display: flex;
  align-items: center;

  label {
    position: relative;
    width: auto;
    max-width: 100%;
    font-family: $font-family-base;
    font-size: $switch-toggle-label-font-size;
    line-height: $switch-toggle-label-line-height;
    cursor: pointer;
    outline: none;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
    @include noneUserSelect();

    /* hide default html checkbox */
    input[type="checkbox"] {
      display: none;
    }

    .#{$prefix}carrier-wrapper {
      display: flex;
      align-items: center;

      &:focus-visible {
        outline: none;
      }

      &:focus-visible::after {
        border: 3px solid v(outlineColor); // ENVIAM 2->3px

        // ENVIAM
        border-radius: 0;
        // END: ENVIAM

        content: "";
        display: block;
        position: absolute;
        left: -5px;
        right: -10px;
        top: -5px;
        bottom: -5px;
      }
    }

    .#{$prefix}icon-carrier {
      position: relative;
      width: $switch-toggle-width;
      height: $switch-toggle-height;
      display: flex;
      justify-content: center;
      flex-shrink: 0;
      flex-grow: 0;
      background-color: v(backgroundColor);
      box-shadow: inset 0px 0px 0px $switch-toggle-border-width v(borderColor);
      border-radius: $switch-toggle-border-radius;
      transition-property: box-shadow, background-color;
      transition-timing-function: ease-in-out;
      transition-duration: 150ms;

      .#{$prefix}icon-content {
        position: absolute;
        left: $switch-toggle-check-margin;
        top: 50%;
        transform: translateY(-50%);
        width: $switch-toggle-check-size;
        height: $switch-toggle-check-size;
        border-radius: $switch-toggle-check-radius;
        background-color: v(iconColor);
        transition-property: visibility, opacity, background-color, left;
        transition-timing-function: ease-in-out;
        transition-duration: 150ms;
      }

      &:hover,
      &.force-hover {
        box-shadow: inset 0 0 0 $switch-toggle-border-hover-width v(iconColorHover);
      }

      &:focus {
        .#{$prefix}icon-content {
          background-color: v(iconColorFocus);
        }
      }

      &.#{$prefix}checked {
        background-color: v(backgroundColorSelected);
        box-shadow: inset 0px 0px 0px $switch-toggle-border-width v(borderColorSelected);

        .#{$prefix}icon-content {
          left: calc(100% - $switch-toggle-check-size - $switch-toggle-check-margin);
          background-color: v(iconColorSelected);
        }

        &:hover,
        &.force-hover {
          box-shadow: inset 0px 0px 0px $switch-toggle-border-hover-width v(iconColorSelectedHover);

          .#{$prefix}icon-content {
            background-color: v(iconColorSelectedHover);
          }

          &::before {
            border-color: v(iconColorSelectedHover);
          }
        }
      }

      &.#{$prefix}disabled {
        background-color: v(backgroundColorDisabled);
        box-shadow: inset 0px 0px 0px $switch-toggle-border-width v(borderColorDisabled);

        .#{$prefix}icon-content {
          background-color: v(iconColorDisabled);
        }

        &.#{$prefix}checked {
          background-color: v(backgroundColorSelectedDisabled);
          box-shadow: inset 0px 0px 0px $switch-toggle-border-width v(borderColorSelectedDisabled);

          .#{$prefix}icon-content {
            background-color: v(iconColorSelectedDisabled);
          }
        }
      }

      &.#{$prefix}invalid {
        box-shadow: inset 0px 0px 0px $switch-toggle-border-width v(errorColor);

        .#{$prefix}icon-content {
          background-color: v(errorColor);
        }

        &:hover,
        &.force-hover {
          box-shadow: inset 0px 0px 0px $switch-toggle-border-hover-width v(errorColor);

          .#{$prefix}icon-content {
            background-color: v(errorColor);
          }

          &::before {
            border-color: v(errorColor);
          }
        }
      }
    }

    .#{$prefix}label {
      color: v(labelColor);
      margin-left: 12px; // ENVIAM
      white-space: normal;
      @include iconTextAligner($switch-toggle-height, $switch-toggle-label-line-height);

      &.#{$prefix}disabled {
        color: v(labelColorDisabled);
      }
    }
  }

  .#{$prefix}switch-toggle-info-icon {
    align-self: flex-start;
    margin-left: 16px;
    // @include iconTextAligner($switch-toggle-height, 20px); // ENVIAM
  }

  &.#{$prefix}small {
    label {
      font-size: $switch-toggle-small-label-font-size; // 16px
      line-height: $switch-toggle-small-label-line-height; // 24px
      min-height: unset;

      .#{$prefix}label {
        margin-left: 8px;
        @include iconTextAligner($switch-toggle-small-height, $switch-toggle-small-label-line-height);
      }

      .#{$prefix}icon-carrier {
        border-radius: $switch-toggle-small-border-radius;
        width: $switch-toggle-small-width;
        height: $switch-toggle-small-height;

        &.#{$prefix}checked {
          box-shadow: inset 0px 0px 0px $switch-toggle-small-border-width v(borderColorSelected);

          &:hover,
          &.force-hover {
            box-shadow: inset 0px 0px 0px $switch-toggle-border-hover-width v(iconColorSelectedHover);
          }

          .#{$prefix}icon-content {
            left: calc(100% - $switch-toggle-small-check-size - $switch-toggle-check-margin);
          }
        }

        &.#{$prefix}disabled {
          box-shadow: inset 0px 0px 0px $switch-toggle-small-border-width v(borderColorDisabled);

          &.#{$prefix}checked {
            box-shadow: inset 0px 0px 0px $switch-toggle-small-border-width v(borderColorSelectedDisabled);

            &:focus {
              box-shadow: inset 0px 0px 0px $switch-toggle-small-border-width v(borderColorSelectedFocus);
            }
          }
        }

        &.#{$prefix}invalid {
          box-shadow: inset 0px 0px 0px $switch-toggle-small-border-width v(errorColor);

          &:hover,
          &.force-hover {
            box-shadow: inset 0px 0px 0px $switch-toggle-border-hover-width v(errorColor);
          }
        }

        .#{$prefix}icon-content {
          width: $switch-toggle-small-check-size;
          height: $switch-toggle-small-check-size;
        }
      }
    }

    .#{$prefix}switch-toggle-info-icon {
      @include iconTextAligner($switch-toggle-small-height, 20px);
    }
  }
}

.#{$prefix}error {
  fill: v(errorColor);
  color: v(labelColor);
}
